/**
 * OptimizationOpportunities - War Room FunnelAssist Integration (MA-AI-OPT-001 UI)
 * 
 * Surfaces pending FunnelAssist recommendations in the War Room dashboard.
 * Shows where the system has detected optimization opportunities.
 * 
 * Features:
 * - Top pending suggestions across all funnels
 * - Quick severity, impact, and confidence indicators
 * - "Review" action to navigate to FunnelAssist panel
 * - "Analyze All Funnels" manual trigger
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sparkles,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Zap,
  Target,
  DollarSign,
  Users,
  Calendar,
  BarChart3,
  ExternalLink,
  Lightbulb,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

// Issue type configuration
const ISSUE_TYPE_CONFIG = {
  low_lead_to_appointment: {
    label: 'Low Booking Rate',
    icon: Calendar,
    color: 'text-amber-400',
    bg: 'bg-amber-500/20',
  },
  high_dropoff_before_booking: {
    label: 'High Drop-off',
    icon: TrendingDown,
    color: 'text-red-400',
    bg: 'bg-red-500/20',
  },
  high_visitors_low_leads: {
    label: 'Low Lead Capture',
    icon: Users,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
  },
  good_leads_weak_profit: {
    label: 'Weak Profitability',
    icon: DollarSign,
    color: 'text-purple-400',
    bg: 'bg-purple-500/20',
  },
  appointments_no_clients: {
    label: 'No Conversions',
    icon: Target,
    color: 'text-red-400',
    bg: 'bg-red-500/20',
  },
};

// Severity configuration
const SEVERITY_CONFIG = {
  high: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', label: 'High', order: 1 },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Medium', order: 2 },
  low: { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Low', order: 3 },
};

// Confidence configuration  
const CONFIDENCE_CONFIG = {
  high: { color: 'text-green-400', label: 'High' },
  medium: { color: 'text-amber-400', label: 'Med' },
  low: { color: 'text-slate-400', label: 'Low' },
};

/**
 * Single Opportunity Row
 */
function OpportunityRow({ suggestion, funnels, onReview }) {
  const issueConfig = ISSUE_TYPE_CONFIG[suggestion.issue_type] || {
    label: suggestion.issue_type,
    icon: Sparkles,
    color: 'text-slate-400',
    bg: 'bg-slate-500/20',
  };
  const IssueIcon = issueConfig.icon;
  
  const severityConfig = SEVERITY_CONFIG[suggestion.severity] || SEVERITY_CONFIG.medium;
  const confidenceConfig = CONFIDENCE_CONFIG[suggestion.confidence] || CONFIDENCE_CONFIG.medium;
  
  // Get funnel name
  const funnel = funnels?.find(f => f.id === suggestion.funnel_id);
  const funnelName = funnel?.name || 'Unknown Funnel';
  
  return (
    <div 
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer",
        "bg-white/[0.02] border border-white/[0.05]",
        "hover:bg-white/[0.04] hover:border-white/[0.1]"
      )}
      onClick={() => onReview(suggestion)}
      data-testid={`opportunity-row-${suggestion.id}`}
    >
      {/* Issue icon */}
      <div className={cn("p-2 rounded-lg flex-shrink-0", issueConfig.bg)}>
        <IssueIcon className={cn("h-4 w-4", issueConfig.color)} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-100 truncate">
            {funnelName}
          </span>
          <Badge 
            variant="outline"
            className={cn("text-[10px] px-1.5", severityConfig.color, severityConfig.border)}
          >
            {severityConfig.label}
          </Badge>
        </div>
        <p className="text-xs text-slate-400 truncate mt-0.5">
          {suggestion.summary}
        </p>
      </div>
      
      {/* Meta */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Impact score */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <BarChart3 className="h-3 w-3" />
                {suggestion.metrics_snapshot?.economic_impact_score?.toFixed(0) || '—'}
              </div>
            </TooltipTrigger>
            <TooltipContent>Economic Impact Score</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* Confidence */}
        <span className={cn("text-xs", confidenceConfig.color)}>
          {confidenceConfig.label}
        </span>
        
        {/* Review arrow */}
        <ChevronRight className="h-4 w-4 text-slate-500" />
      </div>
    </div>
  );
}

/**
 * Main OptimizationOpportunities Component
 */
export function OptimizationOpportunities({ className = '' }) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Fetch suggestions and funnels
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [suggestionsRes, funnelsRes] = await Promise.all([
        api.get('/suggestions?status=pending&limit=10'),
        api.get('/funnels?limit=100'),
      ]);
      
      // Sort by severity (high first), then economic impact, then confidence
      const sorted = (suggestionsRes.data?.suggestions || []).sort((a, b) => {
        // Severity order
        const sevA = SEVERITY_CONFIG[a.severity]?.order || 99;
        const sevB = SEVERITY_CONFIG[b.severity]?.order || 99;
        if (sevA !== sevB) return sevA - sevB;
        
        // Economic impact (higher is better)
        const impactA = a.metrics_snapshot?.economic_impact_score || 0;
        const impactB = b.metrics_snapshot?.economic_impact_score || 0;
        if (impactB !== impactA) return impactB - impactA;
        
        // Confidence
        const confOrder = { high: 1, medium: 2, low: 3 };
        return (confOrder[a.confidence] || 99) - (confOrder[b.confidence] || 99);
      });
      
      setSuggestions(sorted.slice(0, 5)); // Top 5
      setFunnels(funnelsRes.data?.funnels || funnelsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch optimization data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Navigate to funnel details with FunnelAssist tab
  const handleReview = (suggestion) => {
    navigate(`/app/funnels/${suggestion.funnel_id}?tab=assist`);
  };
  
  // Analyze all funnels (manual trigger)
  const handleAnalyzeAll = async () => {
    setAnalyzing(true);
    try {
      // Get all funnels and analyze each
      const funnelsList = funnels.length > 0 ? funnels : (await api.get('/funnels?limit=100')).data?.funnels || [];
      
      let totalSuggestions = 0;
      for (const funnel of funnelsList.slice(0, 10)) { // Limit to 10 funnels
        try {
          const res = await api.post(`/funnels/${funnel.id}/analyze`, {
            max_suggestions: 3,
          });
          totalSuggestions += res.data?.suggestions_generated || 0;
        } catch (e) {
          console.warn(`Analysis skipped for funnel ${funnel.id}:`, e.message);
        }
      }
      
      toast({
        title: 'Analysis complete',
        description: `Generated ${totalSuggestions} new suggestion(s) across your funnels.`,
      });
      
      fetchData();
    } catch (error) {
      toast({
        title: 'Analysis failed',
        description: error?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAnalyzing(false);
    }
  };
  
  return (
    <Card className={cn("glass border-white/10", className)} data-testid="optimization-opportunities">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold text-slate-100 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-400" />
              Optimization Opportunities
            </CardTitle>
            <CardDescription className="text-xs text-slate-400 mt-1">
              AI-detected improvement opportunities
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="h-8 text-xs"
            >
              <RefreshCw className={cn("h-3 w-3 mr-1", loading && "animate-spin")} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleAnalyzeAll}
              disabled={analyzing}
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
              data-testid="analyze-all-funnels-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-3 w-3 mr-1" />
                  Analyze All
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
          </div>
        )}
        
        {/* Empty state */}
        {!loading && suggestions.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <div className="p-2 rounded-full bg-emerald-500/20 mb-3">
              <Sparkles className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-sm text-slate-300 mb-1">No pending opportunities</p>
            <p className="text-xs text-slate-500">
              Click "Analyze All" to scan your funnels
            </p>
          </div>
        )}
        
        {/* Suggestions list */}
        {!loading && suggestions.length > 0 && (
          <div className="space-y-2">
            {suggestions.map((suggestion) => (
              <OpportunityRow
                key={suggestion.id}
                suggestion={suggestion}
                funnels={funnels}
                onReview={handleReview}
              />
            ))}
            
            {/* View all link */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs text-slate-400 hover:text-slate-200"
              onClick={() => navigate('/app/funnels')}
            >
              View all funnels
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact version for sidebar or smaller spaces
 */
export function OptimizationOpportunitiesCompact({ onViewAll }) {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.get('/suggestions?status=pending&limit=1');
        setCount(res.data?.pending_count || 0);
      } catch (e) {
        console.error('Failed to fetch suggestions count:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, []);
  
  if (loading || count === 0) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            onClick={onViewAll || (() => navigate('/app/warroom?tab=overview'))}
            data-testid="optimization-opportunities-badge"
          >
            <Lightbulb className="h-4 w-4 mr-1" />
            <span className="font-medium">{count}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {count} optimization {count === 1 ? 'opportunity' : 'opportunities'} detected
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default OptimizationOpportunities;
